import { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, TrendingDown, Award, Play, Pause, RefreshCw, Sparkles } from 'lucide-react';
import apiClient from '../utils/axios';

export default function VariationManager({ templateId, onVariationGenerated }) {
  const [variations, setVariations] = useState([]);
  const [abTests, setAbTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedTestGroup, setSelectedTestGroup] = useState(null);

  useEffect(() => {
    if (templateId) {
      fetchVariations();
      fetchAbTests();
    }
  }, [templateId]);

  const fetchVariations = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/templates/${templateId}/variations`);
      setVariations(response.data.variations || []);
    } catch (error) {
      console.error('Error fetching variations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAbTests = async () => {
    try {
      const response = await apiClient.get(`/templates/ab-tests`);
      setAbTests(response.data.ab_tests || []);
    } catch (error) {
      console.error('Error fetching A/B tests:', error);
    }
  };

  const handleGenerateVariations = async () => {
    if (!confirm('This will generate 10-20 variations. This may take a few minutes. Continue?')) {
      return;
    }

    try {
      setGenerating(true);
      const response = await apiClient.post(`/templates/${templateId}/generate-variations`, {
        numVariations: 15,
        includeFormats: true,
        includeTones: true,
        includeIndustries: true,
        includeJobTitles: true
      });

      alert(`Successfully generated ${response.data.count} variations!`);
      await fetchVariations();
      await fetchAbTests();
      if (onVariationGenerated) {
        onVariationGenerated(response.data.variations);
      }
    } catch (error) {
      console.error('Error generating variations:', error);
      alert(error.response?.data?.message || 'Error generating variations');
    } finally {
      setGenerating(false);
    }
  };

  const handleSelectWinner = async (variationId) => {
    if (!confirm('Set this variation as the winner? This will mark all others in the test group as non-winners.')) {
      return;
    }

    try {
      await apiClient.post(`/templates/variations/${variationId}/select-winner`);
      alert('Winner selected successfully!');
      await fetchVariations();
      await fetchAbTests();
    } catch (error) {
      console.error('Error selecting winner:', error);
      alert(error.response?.data?.message || 'Error selecting winner');
    }
  };

  const handleAutoSwitch = async (testGroupId) => {
    try {
      const response = await apiClient.post(`/templates/ab-tests/${testGroupId}/auto-switch`);
      if (response.data.winner) {
        alert(`Automatically switched to winning variation! Reply rate: ${response.data.winner.reply_rate}%`);
      } else {
        alert('No statistically significant winner yet. Need more data.');
      }
      await fetchVariations();
      await fetchAbTests();
    } catch (error) {
      console.error('Error auto-switching:', error);
      alert(error.response?.data?.message || 'Error auto-switching');
    }
  };

  const getVariationsForTestGroup = (testGroupId) => {
    return variations.filter(v => v.ab_test_group_id === testGroupId);
  };

  if (loading) {
    return <div className="p-4 text-center text-gray-500">Loading variations...</div>;
  }

  return (
    <div className="space-y-6 bg-purple-50 rounded-lg p-6 border border-purple-200">
      {/* Generate Variations Button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Sparkles size={20} className="text-purple-600" />
          Template Variations & A/B Testing
        </h3>
        <button
          onClick={handleGenerateVariations}
          disabled={generating}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
        >
          {generating ? (
            <>
              <RefreshCw size={16} className="animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles size={16} />
              Generate Variations
            </>
          )}
        </button>
      </div>

      {/* A/B Test Groups */}
      {abTests.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-md font-semibold text-gray-800">Active A/B Tests</h4>
          {abTests.map((test) => {
            const testVariations = getVariationsForTestGroup(test.ab_test_group_id);
            const hasSignificantWinner = test.statistical_significance?.significant;

            return (
              <div key={test.ab_test_group_id} className="bg-white rounded-lg shadow p-4 border border-gray-200">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h5 className="font-semibold text-gray-900">
                      A/B Test Group: {test.ab_test_group_id.substring(0, 8)}...
                    </h5>
                    <p className="text-sm text-gray-500">
                      {test.variation_count} variations • {test.total_sends} total sends
                    </p>
                  </div>
                  {hasSignificantWinner && (
                    <button
                      onClick={() => handleAutoSwitch(test.ab_test_group_id)}
                      className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center gap-1"
                    >
                      <Award size={14} />
                      Auto-Switch to Winner
                    </button>
                  )}
                </div>

                {/* Variation Performance Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {testVariations.map((variation) => (
                    <div
                      key={variation.id}
                      className={`p-4 rounded-lg border-2 ${
                        variation.winning_status
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-xs font-medium text-gray-600 uppercase">
                          {variation.variation_type}
                        </span>
                        {variation.winning_status && (
                          <Award size={16} className="text-green-600" />
                        )}
                      </div>

                      {/* Performance Metrics */}
                      <div className="space-y-2 mt-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Sends:</span>
                          <span className="font-semibold">{variation.sends_count || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Reply Rate:</span>
                          <span className={`font-semibold ${
                            (variation.reply_rate || 0) > (test.avg_reply_rate || 0) ? 'text-green-600' : 'text-gray-900'
                          }`}>
                            {(variation.reply_rate || 0).toFixed(1)}%
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Conversion Rate:</span>
                          <span className={`font-semibold ${
                            (variation.conversion_rate || 0) > (test.avg_conversion_rate || 0) ? 'text-green-600' : 'text-gray-900'
                          }`}>
                            {(variation.conversion_rate || 0).toFixed(1)}%
                          </span>
                        </div>
                      </div>

                      {!variation.winning_status && (
                        <button
                          onClick={() => handleSelectWinner(variation.id)}
                          className="mt-3 w-full px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Set as Winner
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Statistical Significance */}
                {test.statistical_significance && (
                  <div className={`mt-4 p-3 rounded ${
                    test.statistical_significance.significant ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
                  }`}>
                    <p className="text-sm">
                      <strong>Statistical Significance:</strong>{' '}
                      {test.statistical_significance.significant ? (
                        <span className="text-green-700">✓ Significant winner detected</span>
                      ) : (
                        <span className="text-yellow-700">Need more data for statistical significance</span>
                      )}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* All Variations List */}
      {variations.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          <BarChart3 size={48} className="mx-auto mb-4 text-gray-400" />
          <p>No variations generated yet.</p>
          <p className="text-sm mt-2">Click "Generate Variations" to create 10-20 variations for A/B testing.</p>
        </div>
      )}
    </div>
  );
}

